import { useContext, useMemo } from 'react';

import type { WithAnalyticsEventsProps } from '@atlaskit/analytics-next/withAnalyticsEvents';
import type { MentionResource } from '@atlaskit/mention/resource';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
import {
	createFabricMentionResource,
	XProductUserInviteContext,
} from '@confluence/fabric-media-support/entry-points/MentionsProvider.ts';
import { CoreInvitesContext } from '@confluence/core-invites-provider/entry-points/CoreInvitesContext.ts';

import { useSpaceIdPromise } from './useSpaceIdPromise.ts';

export function useMentionProvider(
	spaceId: string | null | undefined,
	withAnalyticsEventProps?: WithAnalyticsEventsProps,
	focusedMentionId?: string,
	options?: {
		includeAgents?: boolean;
	},
): Promise<MentionResource> {
	const { cloudId, userId, isAIEarlyAccessEnabled } = useSessionData();
	const isRovoButtonEnabled = useAIRovoButtonEnablement();
	const { openCoreInvites, userRole } = useContext(CoreInvitesContext);
	const { isEligibleXProductUserInvite, onXProductUserInviteClick } =
		useContext(XProductUserInviteContext); // check if user is eligible for XProductUserInvite

	const spaceIdPromise = useSpaceIdPromise(spaceId);

	return useMemo(
		() => {
			return spaceIdPromise.then((resolvedSpaceId) => {
				return createFabricMentionResource(
					cloudId,
					resolvedSpaceId,
					userId,
					withAnalyticsEventProps,
					openCoreInvites,
					userRole,
					focusedMentionId,
					isEligibleXProductUserInvite,
					onXProductUserInviteClick,
					// Include agent mentions — gated on the AI-disable check (isRovoButtonEnabled,
					// which covers isAdminHubAIEnabled) and AI early access (Labs), since agent
					// mentions are a beta AI feature.
					Boolean(options?.includeAgents && isRovoButtonEnabled && isAIEarlyAccessEnabled),
				);
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			cloudId,
			userId,
			withAnalyticsEventProps,
			openCoreInvites,
			userRole,
			isEligibleXProductUserInvite,
			onXProductUserInviteClick,
			options?.includeAgents,
			isRovoButtonEnabled,
			isAIEarlyAccessEnabled,
		],
	);
}
